import {
  AccountCircle,
  Logout,
  Menu as MenuIcon,
  Settings,
  Check,
  Translate,
} from "@mui/icons-material";
import {
  alpha,
  AppBar,
  Avatar,
  Badge,
  Box,
  Button,
  Chip,
  Divider,
  Hidden,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, matchRoutes, useLocation } from "react-router-dom";
import { AuthState, logout } from "store/features/auth";
import { toggleOpen } from "store/features/sidebar";
import routesConfig from "../../config/routes";
import { stringAvatar } from "utils";
import { toAssetUrl } from "utils/apiConfig";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import { RootState } from "store";
import { fetchProfile, ProfileState } from "store/features/profile";
import { getProfile } from "apis";
import axios from "axios";

const FLAG_MAP: Record<string, string> = {
  en: "../../gb.svg",
  fr: "../../fr.svg",
  ar: "../../ar.svg",
};

const LANGUAGES = [
  { code: "en", native: "English" },
  { code: "fr", native: "Français" },
  { code: "ar", native: "العربية" },
];

const ROLE_THEMES: Record<
  string,
  { gradient: string; badgeKey: string; badgeColor: string }
> = {
  PARENT: {
    gradient: "linear-gradient(135deg, #1976d2, #1565c0)",
    badgeKey: "labels.parent",
    badgeColor: "#1976d2",
  },
  NURSERY_ADMIN: {
    gradient: "linear-gradient(135deg, #6a1b9a, #4a148c)",
    badgeKey: "labels.admin",
    badgeColor: "#6a1b9a",
  },
  ROOM_LEADER: {
    gradient: "linear-gradient(135deg, #00897b, #00695c)",
    badgeKey: "labels.room_leader",
    badgeColor: "#00897b",
  },
  NURSERY_PRACTITIONER: {
    gradient: "linear-gradient(135deg, #00897b, #00695c)",
    badgeKey: "labels.nursery_practitioner",
    badgeColor: "#00897b",
  },
};

const Topbar: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [lng, setLng] = useState(i18n.language);
  const dir = i18n.dir(i18n.language);
  i18n.on("languageChanged", (lng) => setLng(`${lng}`));
  const { loaded, item: profile } = useSelector<RootState, ProfileState>(
    (store) => store.profile,
  );
  const { role, token, nursery } = useSelector<RootState, AuthState>(
    (store) => store.auth,
  );
  const location = useLocation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const roleTheme = ROLE_THEMES[role || "PARENT"] || ROLE_THEMES.PARENT;

  useEffect(() => {
    const interceptor = axios.interceptors.request.use((config) => {
      config.headers["accept-language"] = i18n.language;
      return config;
    });
    return () => axios.interceptors.request.eject(interceptor);
  }, [i18n.language]);

  useEffect(() => {
    if (!token || loaded) return;
    getProfile()
      .then(({ data }) => {
        dispatch(fetchProfile(data));
      })
      .catch(() => {});
  }, [dispatch, token, loaded]);

  const label = useMemo(() => {
    const matches = matchRoutes(routesConfig, location);
    return matches && matches[0] && matches[0].route.label;
  }, [location]);

  useEffect(() => {
    document.documentElement.dir = dir;
  }, [dir]);

  const handleToggleSidebar = useCallback(() => {
    dispatch(toggleOpen());
  }, [dispatch]);

  const [profileAnchor, setProfileAnchor] = useState<null | HTMLElement>(null);
  const [langAnchor, setLangAnchor] = useState<null | HTMLElement>(null);

  const handleLogout = useCallback(() => {
    setProfileAnchor(null);
    dispatch(logout());
  }, [dispatch]);

  const profileName = profile
    ? `${profile.firstName} ${profile.lastName}`.trim()
    : "";

  return (
    <AppBar sx={{ position: "sticky" }}>
      <Toolbar
        sx={{
          background: roleTheme.gradient,
          [theme.breakpoints.down("sm")]: { px: 1 },
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          width="100%"
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Hidden smUp>
              <IconButton
                onClick={handleToggleSidebar}
                sx={{ color: "common.white" }}
              >
                <MenuIcon />
              </IconButton>
            </Hidden>
            <Typography
              variant="h6"
              color="common.white"
              fontWeight={600}
              noWrap
            >
              {label === "posts.transactions"
                ? t(`${label}`)
                : t(`${label}.label`)}
            </Typography>
            <Chip
              label={t(roleTheme.badgeKey)}
              size="small"
              sx={{
                bgcolor: alpha(theme.palette.common.white, 0.18),
                color: "common.white",
                fontWeight: 700,
                fontSize: 10,
                height: 20,
                letterSpacing: 0.5,
                textTransform: "uppercase",
              }}
            />
          </Box>

          {/* Right-hand actions */}
          <Box display="flex" alignItems="center" gap={0.5}>
            {/* Language picker */}
            <Hidden smDown>
              <Button
                onClick={(e) => setLangAnchor(e.currentTarget)}
                size="small"
                sx={{
                  minWidth: 0,
                  px: 1.2,
                  py: 0.6,
                  borderRadius: 5,
                  bgcolor: alpha(theme.palette.common.white, 0.12),
                  color: "common.white",
                  textTransform: "uppercase",
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  gap: 0.8,
                  "&:hover": {
                    bgcolor: alpha(theme.palette.common.white, 0.22),
                  },
                  transition: "background-color 0.2s",
                }}
              >
                <Box
                  sx={{
                    width: 18,
                    height: 18,
                    borderRadius: "50%",
                    overflow: "hidden",
                    flexShrink: 0,
                    border: `1.5px solid ${alpha(theme.palette.common.white, 0.5)}`,
                  }}
                >
                  <img
                    src={FLAG_MAP[lng] ?? FLAG_MAP.en}
                    alt={lng}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                </Box>
                {lng}
              </Button>
              <Menu
                open={Boolean(langAnchor)}
                anchorEl={langAnchor}
                onClose={() => setLangAnchor(null)}
                PaperProps={{
                  sx: {
                    borderRadius: 2.5,
                    minWidth: 200,
                    mt: 1,
                    py: 0.5,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                  },
                }}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
              >
                <Typography
                  variant="caption"
                  color="text.disabled"
                  sx={{
                    px: 2,
                    py: 0.5,
                    display: "block",
                    textTransform: "uppercase",
                    letterSpacing: 1,
                    fontWeight: 700,
                    fontSize: 10,
                  }}
                >
                  {t("languages")}
                </Typography>
                {LANGUAGES.map(({ code, native }) => (
                  <MenuItem
                    key={code}
                    onClick={() => {
                      i18n.changeLanguage(code);
                      setLangAnchor(null);
                    }}
                    sx={{
                      borderRadius: 1.5,
                      mx: 0.75,
                      py: 1,
                      my: 0.25,
                      ...(lng === code && {
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                        "&:hover": {
                          bgcolor: alpha(theme.palette.primary.main, 0.14),
                        },
                      }),
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: "50%",
                          overflow: "hidden",
                          border: `1.5px solid ${lng === code ? theme.palette.primary.main : theme.palette.divider}`,
                          transition: "border-color 0.2s",
                        }}
                      >
                        <img
                          src={FLAG_MAP[code]}
                          alt={native}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            display: "block",
                          }}
                        />
                      </Box>
                    </ListItemIcon>
                    <ListItemText>
                      <Typography
                        variant="body2"
                        fontWeight={lng === code ? 600 : 400}
                      >
                        {native}
                      </Typography>
                    </ListItemText>
                    {lng === code && (
                      <Check
                        sx={{ fontSize: 18, color: "primary.main", ml: 1 }}
                      />
                    )}
                  </MenuItem>
                ))}
              </Menu>
            </Hidden>

            {/* Separator */}
            <Hidden smDown>
              <Divider
                orientation="vertical"
                flexItem
                sx={{
                  mx: 0.5,
                  borderColor: alpha(theme.palette.common.white, 0.2),
                  height: 24,
                  alignSelf: "center",
                }}
              />
            </Hidden>

            {/* Nursery context — visible always when known */}
            {nursery?.name && (
              <Hidden smDown>
                <Chip
                  label={nursery.name}
                  size="small"
                  sx={{
                    bgcolor: alpha(theme.palette.common.white, 0.18),
                    color: "common.white",
                    fontWeight: 600,
                    fontSize: 12,
                    maxWidth: 200,
                    "& .MuiChip-label": {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                />
              </Hidden>
            )}

            {/* Profile */}
            <Hidden smDown>
              <Tooltip
                title={
                  [profileName, nursery?.name].filter(Boolean).join(" · ") ||
                  t("profile.label")
                }
              >
                <IconButton
                  onClick={(e) => setProfileAnchor(e.currentTarget)}
                  sx={{
                    p: 0.5,
                    "&:hover": {
                      bgcolor: alpha(theme.palette.common.white, 0.1),
                    },
                  }}
                >
                  <Avatar
                    src={toAssetUrl(profile?.photo as string | undefined)}
                    {...(!profile?.photo && profileName
                      ? stringAvatar(profileName, {
                          width: 34,
                          height: 34,
                          fontSize: 14,
                          border: `2px solid ${alpha(theme.palette.common.white, 0.4)}`,
                          transition: "border-color 0.2s",
                          "&:hover": { borderColor: "common.white" },
                        })
                      : {
                          sx: {
                            width: 34,
                            height: 34,
                            border: `2px solid ${alpha(theme.palette.common.white, 0.4)}`,
                          },
                        })}
                  />
                </IconButton>
              </Tooltip>

              <Menu
                open={Boolean(profileAnchor)}
                anchorEl={profileAnchor}
                onClose={() => setProfileAnchor(null)}
                PaperProps={{
                  sx: {
                    borderRadius: 2,
                    minWidth: 220,
                    mt: 1,
                    overflow: "visible",
                  },
                }}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
              >
                {/* User info header */}
                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                  }}
                >
                  <Avatar
                    src={toAssetUrl(profile?.photo as string | undefined)}
                    {...(!profile?.photo && profileName
                      ? stringAvatar(profileName, {
                          width: 40,
                          height: 40,
                          fontSize: 16,
                        })
                      : { sx: { width: 40, height: 40 } })}
                  />
                  <Box>
                    <Box display="flex" alignItems="center" gap={0.75}>
                      <Typography variant="subtitle2" fontWeight={600} noWrap>
                        {profileName}
                      </Typography>
                      <Chip
                        label={t(roleTheme.badgeKey)}
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: 9,
                          fontWeight: 700,
                          bgcolor: alpha(roleTheme.badgeColor, 0.12),
                          color: roleTheme.badgeColor,
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                        }}
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {profile?.email}
                    </Typography>
                    {nursery?.name && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        noWrap
                        sx={{ display: "block", fontWeight: 600 }}
                      >
                        {nursery.name}
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Divider sx={{ my: 0.5 }} />

                <MenuItem
                  component={Link}
                  to="/profile"
                  onClick={() => setProfileAnchor(null)}
                  sx={{ borderRadius: 1, mx: 0.5, py: 1 }}
                >
                  <ListItemIcon>
                    <AccountCircle fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>{t("profile.label")}</ListItemText>
                </MenuItem>

                <MenuItem
                  component={Link}
                  to="/settings"
                  onClick={() => setProfileAnchor(null)}
                  sx={{ borderRadius: 1, mx: 0.5, py: 1 }}
                >
                  <ListItemIcon>
                    <Settings fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>{t("setting.label")}</ListItemText>
                </MenuItem>

                <Divider sx={{ my: 0.5 }} />

                <MenuItem
                  onClick={handleLogout}
                  sx={{ borderRadius: 1, mx: 0.5, py: 1, color: "error.main" }}
                >
                  <ListItemIcon>
                    <Logout fontSize="small" sx={{ color: "error.main" }} />
                  </ListItemIcon>
                  <ListItemText>{t("logout")}</ListItemText>
                </MenuItem>
              </Menu>
            </Hidden>
          </Box>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Topbar;
